// Tejal Komb //
import {
  Heading,
  Box,
  Flex,
  Image,
  Text,
  VStack,
  Center,
  Button,
  IconButton,
} from "@chakra-ui/react";

import { motion, AnimatePresence  } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import Navbar from "../components/NavBar";
import Footer from "../components/Footer";
import imgHero from "../images/teams.png";
import dermalensImg from "../images/phones.svg";
import autobuddysImg from "../images/autobuddys.jpg";
import googlePlay from "../images/google-play.png";
import appStore from "../images/app-store.png";
import govInAppStore from "../images/gov-in.png";
import Lottie from "lottie-react";
import colleaguesAnimation from "../images/lottie/colleagues-lottie.json"; // adjust path if needed
import { GraduationCap, Users, BriefcaseBusiness, Trophy, Sparkles, Code, Palette } from "lucide-react";
import HeroSection from "../components/HeroSection";
import WhatisGyan from "./WhatisGyan";
import Benefits from "./Benefits";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);
const MotionFlex = motion(Flex);

const perks = [
  { icon: <Code size={18} />, label: "Real-World Projects" },
  { icon: <GraduationCap size={18} />, label: "Completion Certificate & Performance-Based LOR" },
  { icon: <Palette size={18} />, label: "Mentorship" },
  { icon: <Sparkles size={18} />, label: "Career Growth" },
];

const badges = [
  {
    icon: <Users size={18} />,
    value: "500+",
    text: "Assisted Students",
    bg: "blue.400",
    delay: 0,
  },
  {
    icon: <BriefcaseBusiness size={18} />,
    value: "50+",
    text: "Internships Available",
    bg: "green.400",
    delay: 0.15,
  },
  {
    icon: <Trophy size={18} />,
    value: "Congratulations",
    text: "You are selected",
    bg: "orange.400",
    delay: 0.3,
  },
];

function Landing() {
  const [currentStartup, setCurrentStartup] = useState(0);
const [direction, setDirection] = useState(0);

  const startups = [
    {
      name: "DermaLens",
      company: "By AiM4u Software Solutions",
      description:
        "A Patented AI and Deep Learning-powered Skincare Assistant that analyzes your skin concerns and provides instant access to dermatologists anytime, anywhere.Our algorithm detects skin diseases with 95% accuracy.",
      image: dermalensImg,
      playStoreLink: "https://play.google.com/store/apps/details?id=com.aim4u.dermalens",
      appStoreLink: "https://apps.apple.com/in/app/dermalens-by-aim4u/id6476604789",
    },
    {
      name: "AutoBuddys",
      company: "By AiM4u Software Solutions",
      description:
        "'AutoBuddys' is a thoughtfully designed and inclusive mobile application crafted specifically for children on the autism spectrum. With a focus on providing a supportive and engaging environment, this app aims to assist autistic children in developing essential skills while catering to their unique sensory needs.",
      image: autobuddysImg,
      playStoreLink: "https://play.google.com/store/apps/details?id=com.aim4u.autobuddys",
      appStoreLink: "https://apps.apple.com/us/app/autobuddys/id6476968391",
      govStore: "https://apps.mgov.gov.in/app-info/2173",
    },
  ];

  const imageSizes = {
    AutoBuddys: { base: "60%", sm: "40%", md: "23%", lg: "12%" },
    DermaLens: { base: "80%", sm: "60%", md: "40%", lg: "20%" },
  };

  const handleNext = () => {
    setCurrentStartup((prev) => (prev + 1) % startups.length);
  };

  const handlePrev = () => {
    setCurrentStartup((prev) => (prev - 1 + startups.length) % startups.length);
  };

  return (
    <Box fontFamily="SF Pro Display, sans-serif">
      <Navbar />
<HeroSection />
<WhatisGyan />
<Benefits />
   
  <Box
  py={24}
  px={4}
  position="relative"
  overflow="hidden"
  bgGradient="linear(to-br, #163a7a, #1e4b9e, #0c2252)"
  color="white"
  fontFamily="Figtree, sans-serif"
>

  {/* glow blobs */}
  <Box position="absolute" top="10%" left="15%" w="500px" h="500px" bg="blue.400" opacity={0.15} filter="blur(120px)" />
  <Box position="absolute" bottom="0" right="10%" w="400px" h="400px" bg="green.400" opacity={0.12} filter="blur(120px)" />

  {/* heading */}
  <MotionBox
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    textAlign="center"
    mb={16}
  >
    <Heading fontSize={{ base: "3xl", md: "5xl" }} fontWeight="black">
      Explore Our{" "}
      <Text as="span" bgGradient="linear(to-r, green.300, teal.200)" bgClip="text">
        Other Services
      </Text>
    </Heading>

    <Text mt={4} fontSize="lg" color="whiteAlpha.700">
      Products built by AiM4u that are already impacting real users
    </Text>
  </MotionBox>

  <Flex align="center" justify="center" position="relative">

    {/* LEFT ARROW */}
    <IconButton
      aria-label="Previous"
      icon={<ArrowLeftIcon />}
      onClick={() => {
        setDirection(-1);
        handlePrev();
      }}
      position="absolute"
      left={{ base: 2, lg: 8 }}
      top="50%"
      transform="translateY(-50%)"
      bg="whiteAlpha.200"
      backdropFilter="blur(10px)"
      borderRadius="full"
      boxSize="52px"
      _hover={{ bg: "green.400", transform: "translateY(-50%) scale(1.15)" }}
      transition="0.25s"
      zIndex={2}
    />

    {/* CARD WITH SLIDE ANIMATION */}
    <AnimatePresence mode="wait" custom={direction}>
      <MotionFlex
        key={currentStartup}
        custom={direction}
        initial={{ opacity: 0, x: direction > 0 ? 120 : -120, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: direction > 0 ? -120 : 120, scale: 0.95 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        align="center"
        justify="center"
        w="full"
        maxW="1200px"
        px={{ base: 6, lg: 14 }}
        py={12}
        gap={{ base: 10, lg: 16 }}
        direction={{ base: "column", lg: "row" }}
        bg="whiteAlpha.100"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255,255,255,.18)"
        borderRadius="3xl"
        boxShadow="0 40px 90px rgba(0,0,0,.4)"
      >

        {/* BIGGER PHONE IMAGE */}
        <MotionBox
          flex="1"
          display="flex"
          justifyContent="center"
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 0.4 }}
        >
          <Image
            src={startups[currentStartup].image}
            alt={startups[currentStartup].name}
            maxH={{ base: "360px", md: "520px" }}
            objectFit="contain"
            filter="drop-shadow(0px 30px 60px rgba(0,0,0,.45))"
          />
        </MotionBox>

        {/* TEXT SIDE */}
        <MotionVStack
          flex="1"
          align={{ base: "center", lg: "start" }}
          spacing={6}
          maxW="540px"
        >

          <Text fontSize="4xl" fontWeight="black">
            {startups[currentStartup].name}
          </Text>

          <Text fontWeight="semibold" color="whiteAlpha.700">
            {startups[currentStartup].company}
          </Text>

          <Text fontSize="lg" color="whiteAlpha.800" lineHeight="tall">
            {startups[currentStartup].description}
          </Text>

          {/* store buttons */}
     <Flex
  gap={5}
  pt={4}
  wrap="wrap"
  align="center"
  justify={{ base: "center", lg: "flex-start" }}
>
  <motion.img
    whileHover={{ scale: 1.12 }}
    src={googlePlay}
    style={{ height: 185 , cursor: "pointer" }}
  />
  <motion.img
    whileHover={{ scale: 1.12 }}
    src={appStore}
    style={{ height: 55, cursor: "pointer" }}
  />
</Flex>

        </MotionVStack>
      </MotionFlex>
    </AnimatePresence>

    {/* RIGHT ARROW */}
    <IconButton
      aria-label="Next"
      icon={<ArrowRightIcon />}
      onClick={() => {
        setDirection(1);
        handleNext();
      }}
      position="absolute"
      right={{ base: 2, lg: 8 }}
      top="50%"
      transform="translateY(-50%)"
      bg="whiteAlpha.200"
      backdropFilter="blur(10px)"
      borderRadius="full"
      boxSize="52px"
      _hover={{ bg: "green.400", transform: "translateY(-50%) scale(1.15)" }}
      transition="0.25s"
      zIndex={2}
    />

  </Flex>
</Box>

      <Footer />
    </Box>
  );
}

export default Landing;